import { render, fireEvent, waitFor } from '@testing-library/react';
import React from 'react';
import ApiGatewayLogin from './ApiGatewayLogin';
import { postCheckUsernameSso } from './services';

jest.mock('./services');

describe('ApiGatewayLogin', () => {
  const oauthParams = {
    client_id: 'client-123',
    redirect_uri: 'https://client.example.com/callback',
    code_challenge: 'challenge-abc',
    code_challenge_method: 'S256',
    state: 'state-xyz',
    response_type: 'code',
  };

  const translations = {
    portalLogin: 'Portal Login',
    usernameOrEmail: 'Username or Email',
    password: 'Password',
    forgotPassword: 'Forgot Password',
    logIn: 'Log in',
    legalAgreementsHtml:
      'By logging into the application you agree to the Policies &amp; Agreements.',
  };

  const defaultProps = {
    csrfToken: 'test-csrf-token',
    formAction: '/auth/api_gateway',
    oauthParams,
    translations,
    requestPasswordClick: jest.fn(),
  };

  const renderComponent = (props = {}) =>
    render(<ApiGatewayLogin {...defaultProps} {...props} />);

  const mockSsoCheck = (body) => {
    postCheckUsernameSso.mockResolvedValue({ json: async () => body });
  };

  const typeIdentity = (container, value = 'jane.doe') =>
    fireEvent.change(container.querySelector('input[name="auth_key"]'), {
      target: { value },
    });

  afterEach(() => {
    jest.restoreAllMocks();
    jest.clearAllMocks();
  });

  it('renders a POST form targeting the api_gateway action', () => {
    const { container } = renderComponent();
    const form = container.querySelector('form');

    expect(form).toBeInTheDocument();
    expect(form.getAttribute('method')).toBe('post');
    expect(form.getAttribute('action')).toBe('/auth/api_gateway');
  });

  it('renders a hidden CSRF token input and all six OAuth params', () => {
    const { container } = renderComponent();

    const csrf = container.querySelector('input[name="authenticity_token"]');
    expect(csrf).toBeInTheDocument();
    expect(csrf.value).toBe('test-csrf-token');

    Object.entries(oauthParams).forEach(([name, value]) => {
      const input = container.querySelector(`input[name="${name}"]`);
      expect(input).toBeInTheDocument();
      expect(input.getAttribute('type')).toBe('hidden');
      expect(input.value).toBe(value);
    });
  });

  it('starts on the identity step with the password hidden and Log In disabled', () => {
    const { container, getByText } = renderComponent();

    expect(
      container.querySelector('input[name="auth_key"]'),
    ).toBeInTheDocument();
    expect(
      container.querySelector('input[name="password"]'),
    ).not.toBeInTheDocument();
    expect(getByText('Log in').closest('button')).toBeDisabled();
  });

  it('runs the SSO username-check when Log In is clicked with an identity', async () => {
    mockSsoCheck({ requires_sso: false, allow_password_login: true });
    const { container, getByText } = renderComponent();

    typeIdentity(container);
    fireEvent.click(getByText('Log in'));

    await waitFor(() => {
      expect(postCheckUsernameSso).toHaveBeenCalledWith(
        'test-csrf-token',
        'jane.doe',
      );
    });
  });

  it('reveals the password field when password login is allowed', async () => {
    mockSsoCheck({ requires_sso: false, allow_password_login: true });
    const { container, getByText } = renderComponent();

    typeIdentity(container);
    fireEvent.click(getByText('Log in'));

    await waitFor(() => {
      expect(
        container.querySelector('input[name="password"]'),
      ).toBeInTheDocument();
    });
  });

  it('redirects to the SSO provider when the user requires SSO', async () => {
    mockSsoCheck({ requires_sso: true, redirect_url: '/auth/pcaraz' });
    delete window.location;
    window.location = { href: '' };

    const { container, getByText } = renderComponent();
    typeIdentity(container, 'sso.user');
    fireEvent.click(getByText('Log in'));

    await waitFor(() => {
      expect(window.location.href).toBe('/auth/pcaraz');
    });
  });

  it('performs a native form submit (not a fetch) on the password step', async () => {
    mockSsoCheck({ requires_sso: false, allow_password_login: true });
    const submitSpy = jest
      .spyOn(HTMLFormElement.prototype, 'submit')
      .mockImplementation(() => {});

    const { container, getByText } = renderComponent();
    typeIdentity(container);
    fireEvent.click(getByText('Log in'));

    await waitFor(() => {
      expect(
        container.querySelector('input[name="password"]'),
      ).toBeInTheDocument();
    });
    fireEvent.change(container.querySelector('input[name="password"]'), {
      target: { value: 'Password1!' },
    });
    fireEvent.click(getByText('Log in'));

    expect(submitSpy).toHaveBeenCalledTimes(1);
  });

  it('does not submit when required fields are empty (native validation blocks it)', async () => {
    mockSsoCheck({ requires_sso: false, allow_password_login: true });
    const submitSpy = jest
      .spyOn(HTMLFormElement.prototype, 'submit')
      .mockImplementation(() => {});
    jest
      .spyOn(HTMLFormElement.prototype, 'reportValidity')
      .mockReturnValue(false);

    const { container, getByText } = renderComponent();
    typeIdentity(container);
    fireEvent.click(getByText('Log in'));

    await waitFor(() => {
      expect(
        container.querySelector('input[name="password"]'),
      ).toBeInTheDocument();
    });
    fireEvent.click(getByText('Log in'));

    expect(submitSpy).not.toHaveBeenCalled();
  });

  it('switches to the password reset screen when Forgot password is clicked', async () => {
    mockSsoCheck({ requires_sso: false, allow_password_login: true });
    const requestPasswordClick = jest.fn();
    const { container, getByText } = renderComponent({ requestPasswordClick });

    typeIdentity(container);
    fireEvent.click(getByText('Log in'));

    await waitFor(() => {
      expect(getByText('Forgot Password')).toBeInTheDocument();
    });
    fireEvent.click(getByText('Forgot Password'));

    expect(requestPasswordClick).toHaveBeenCalledTimes(1);
  });
});
